import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { defineMessages, useIntl } from 'react-intl';
import Button from '@/shared/components/Button';
import { DeleteIcon } from '@/shared/components/Icons';
import YesNoModal from '@/shared/components/YesNoModal';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
const messages = defineMessages({
    CompanyTeams: {
        id: 'CompanyTeams',
        defaultMessage: 'Company teams',
    },
    CompanyTeamsWarning: {
        id: 'CompanyTeamsWarning',
        defaultMessage: 'Changing the teams assigned to this company may affect access and visibility for users. Make sure the selection is correct before saving.',
    },
    AddTeam: {
        id: 'AddTeam',
        defaultMessage: 'Add a team',
    },
    Add: {
        id: 'Add',
        defaultMessage: 'Add',
    },
    Remove: {
        id: 'Remove',
        defaultMessage: 'Remove',
    },
    ConfirmRemoveTeamTitle: {
        id: 'ConfirmRemoveTeamTitle',
        defaultMessage: 'Remove team',
    },
    ConfirmRemoveTeam: {
        id: 'ConfirmRemoveTeam',
        defaultMessage: 'Are you sure you want to remove the team "{name}" from this company?',
    },
    Close: {
        id: 'Close',
        defaultMessage: 'Close',
    },
    ApplyingTeamChanges: {
        id: 'ApplyingTeamChanges',
        defaultMessage: 'Applying the team change to the relations of this company, please wait...',
    },
});
// JobStatus: Initialized = 0, Processing = 1, Success = 2, Failed = 3, Skipped = 4
const JOB_FINISHED_MIN_STATUS = 2;
const RemoveIcon = DeleteIcon;
const CompanyTeamsModal = ({ show, onClose, companyId, teamItems, companyTeams, onTeamsChanged, }) => {
    const intl = useIntl();
    const [busy, setBusy] = useState(false);
    const [pendingJobId, setPendingJobId] = useState(null);
    const [teamToRemove, setTeamToRemove] = useState(null);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const pendingJobRef = useRef(null);
    const blocked = busy || pendingJobId != null;
    useEffect(() => {
        const handler = (job) => {
            if ((job === null || job === void 0 ? void 0 : job.id) === pendingJobRef.current && job.status >= JOB_FINISHED_MIN_STATUS) {
                pendingJobRef.current = null;
                setPendingJobId(null);
            }
        };
        SignalRService.subscribe('jobUpdated', handler);
        return () => SignalRService.unsubscribe('jobUpdated', handler);
    }, []);
    const availableItems = teamItems.filter(t => !companyTeams.some(ct => ct.id === t.id));
    const applyChange = async (newTeams, request) => {
        setBusy(true);
        try {
            const { data } = await request();
            onTeamsChanged(newTeams, data.teamSecurityCode);
            if ((data === null || data === void 0 ? void 0 : data.job) && data.job.status < JOB_FINISHED_MIN_STATUS) {
                pendingJobRef.current = data.job.id;
                setPendingJobId(data.job.id);
            }
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        finally {
            setBusy(false);
        }
    };
    const handleAdd = (teamId) => {
        const item = teamItems.find(t => t.id === teamId);
        if (!item)
            return;
        const newTeams = [...companyTeams, { id: item.id, name: item.label, code: item.code }];
        applyChange(newTeams, () => api.base.post(`/api/Team/AddCompanyTeam/${companyId}/${teamId}`));
        setSelectedTeamId('');
    };
    const handleRemove = async (teamId) => {
        const newTeams = companyTeams.filter(t => t.id !== teamId);
        await applyChange(newTeams, () => api.base.delete(`/api/Team/DeleteCompanyTeam/${companyId}/${teamId}`));
        setTeamToRemove(null);
    };
    return (_jsxs(Modal, Object.assign({ show: show, onHide: blocked ? () => undefined : onClose, backdrop: "static", keyboard: !blocked }, { children: [_jsx(Modal.Header, Object.assign({ closeButton: !blocked }, { children: _jsx(Modal.Title, { children: intl.formatMessage(messages.CompanyTeams) }) })), _jsxs(Modal.Body, { children: [_jsxs("div", Object.assign({ className: "input-group" }, { children: [_jsxs("select", Object.assign({ className: "form-control", value: selectedTeamId, onChange: e => setSelectedTeamId(e.target.value), disabled: blocked || availableItems.length === 0 }, { children: [_jsx("option", Object.assign({ value: "" }, { children: intl.formatMessage(messages.AddTeam) })), availableItems.map(item => (_jsx("option", Object.assign({ value: item.id }, { children: item.label }), item.id)))] })), _jsx("span", Object.assign({ className: "input-group-btn" }, { children: _jsx(Button, Object.assign({ onClick: () => handleAdd(selectedTeamId), disabled: blocked || !selectedTeamId }, { children: intl.formatMessage(messages.Add) })) }))] })), pendingJobId != null && (_jsxs("p", Object.assign({ className: "help-block margin5T" }, { children: [_jsx("i", { className: "fa fa-spinner fa-spin margin5R" }), intl.formatMessage(messages.ApplyingTeamChanges)] }))), _jsx("div", Object.assign({ className: "margin20T" }, { children: _jsx("ul", Object.assign({ className: "list-unstyled" }, { children: companyTeams.map(team => (_jsxs("li", Object.assign({ className: "margin5B" }, { children: [_jsx("span", Object.assign({ className: "margin5R" }, { children: team.name })), !blocked && (_jsx(RemoveIcon, { tooltip: intl.formatMessage(messages.Remove), onClick: () => setTeamToRemove(team), style: { cursor: 'pointer' } }))] }), team.id))) })) })), _jsxs("p", Object.assign({ className: "help-block text-danger margin20T" }, { children: [_jsx("i", { className: "fa fa-exclamation-triangle margin5R" }), intl.formatMessage(messages.CompanyTeamsWarning)] }))] }), _jsx(Modal.Footer, { children: _jsx(Button, Object.assign({ onClick: onClose, disabled: blocked }, { children: intl.formatMessage(messages.Close) })) }), teamToRemove && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(messages.ConfirmRemoveTeamTitle), message: intl.formatMessage(messages.ConfirmRemoveTeam, { name: teamToRemove.name }), onSave: () => handleRemove(teamToRemove.id), onNo: () => setTeamToRemove(null), onClose: () => setTeamToRemove(null), disabled: blocked }))] })));
};
export default CompanyTeamsModal;
